import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Container, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useGetAllUsersQuery, useCreateTestUsersMutation } from '../slices/usersAdminApiSlice';

const TestAdminScreen = () => {
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);
  
  const [numberOfUsers, setNumberOfUsers] = useState(5);
  const [isAdmin, setIsAdmin] = useState(false);

  const { data: users } = useGetAllUsersQuery();
  const [createTestUsers, { isLoading: isCreating }] = useCreateTestUsersMutation();

  useEffect(() => {
    // Check if user is logged in
    if (!userInfo) {
      navigate('/login');
      return;
    }

    // Check if user is admin
    if (users) {
      const currentUserData = users.find(userData => userData.user._id === userInfo._id);
      if (currentUserData?.athlete?.isAdmin) {
        setIsAdmin(true);
      } else {
        toast.error('Not authorized as admin');
        navigate('/');
      }
    }
  }, [userInfo, navigate, users]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (numberOfUsers < 1 || numberOfUsers > 100) {
      toast.error('Number of users must be between 1 and 100');
      return;
    }

    const confirmed = window.confirm(
      `This will DELETE ALL DATA in the database and create ${numberOfUsers} test users. Are you sure?`
    );

    if (!confirmed) {
      return;
    }

    try {
      const result = await createTestUsers({ numberOfUsers }).unwrap();
      toast.success(result.message);
      console.log('Created users:', result.users);
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to create test users');
    }
  };

  const handleNumberChange = (e) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value)) {
      setNumberOfUsers(value);
    }
  };

  if (!isAdmin) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          You must be an admin to access this page.
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-5">
      <div className="row justify-content-center">
        <div className="col-md-6">
          <Card>
            <Card.Header className="bg-danger text-white">
              <h3 className="mb-0">Test User Creation</h3>
            </Card.Header>
            <Card.Body>
              <Alert variant="warning">
                <strong>Warning:</strong> This action will permanently delete ALL data in the database including:
                <ul className="mt-2 mb-0">
                  <li>All users</li>
                  <li>All athletes</li>
                  <li>All teams</li>
                  <li>All leagues</li>
                  <li>All drafts</li>
                  <li>All invitations</li>
                  <li>All events</li>
                </ul>
              </Alert>

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-4">
                  <Form.Label>Number of Test Users to Create</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max="100"
                    value={numberOfUsers}
                    onChange={handleNumberChange}
                    placeholder="Enter number of users (1-100)"
                    required
                  />
                  <Form.Text className="text-muted">
                    First user will be set as admin. Usernames will be user1, user2, etc.
                    All passwords will be @Ndrew1597
                  </Form.Text>
                </Form.Group>

                <div className="d-grid">
                  <Button 
                    type="submit" 
                    variant="danger" 
                    size="lg"
                    disabled={isCreating}
                  >
                    {isCreating ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Creating Users...
                      </>
                    ) : (
                      'Clear Database & Create Test Users'
                    )}
                  </Button>
                </div>
              </Form>

              <div className="mt-4">
                <h5>Test User Details:</h5>
                <ul className="list-unstyled">
                  <li><strong>Usernames:</strong> user1, user2, user3, ...</li>
                  <li><strong>Emails:</strong> user1@test.com, user2@test.com, ...</li>
                  <li><strong>First Names:</strong> user1fn, user2fn, user3fn, ...</li>
                  <li><strong>Last Names:</strong> user1ln, user2ln, user3ln, ...</li>
                  <li><strong>Password:</strong> @Ndrew1597 (for all users)</li>
                  <li><strong>Admin:</strong> First user only (user1)</li>
                </ul>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>
    </Container>
  );
};

export default TestAdminScreen;
